import React, { useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { get, post, upload } from "../lib/api";
import { useDocumentTitle, useLoad, useQueryState } from "../lib/hooks";
import { formatDateTime } from "../../helpers/datetime";
import { Badge, Button, Card, Empty, ErrorBox, Loading, Modal, PageHead, Pager, Segment, Spinner, Tabs } from "../ui";
import { KIND_LABEL, ReportCounts, ReportRows } from "../components/ImportReport";
import Duplicates from "./Duplicates";
import { useShell } from "../shell/context";

const SOURCES = [
    { value: "linkedin", label: "LinkedIn export (.csv)", accept: ".csv,text/csv", route: "v1/crm/imports/linkedin" },
    { value: "tracker", label: "Tracker workbook (.xlsx)", accept: ".xlsx", route: "v1/crm/imports/tracker" },
];

function Steps({ source }) {
    if (source === "tracker") {
        return (
            <p className="hz-muted" style={{ margin: "0 0 14px" }}>
                One-off migration of the tracker workbook (People, Touch Log, Leads, Candidates tabs). Columns are matched by header name; the preview lists unknown columns and rows the mapping cannot read.
            </p>
        );
    }
    return (
        <ol style={{ margin: "0 0 14px", paddingLeft: 18, color: "var(--hz-gray-700)", lineHeight: 1.7 }}>
            <li>On LinkedIn, open <b>Settings, Data privacy, Get a copy of your data</b>.</li>
            <li>Pick <b>Connections</b>, <b>Invitations</b> and <b>Messages</b>. LinkedIn emails a zip.</li>
            <li>Upload <b>Connections.csv</b> first, then Invitations.csv and messages.csv, one at a time.</li>
        </ol>
    );
}

function Upload() {
    const { toast, bump } = useShell();
    const history = useHistory();
    const [source, setSource] = useState("linkedin");
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [busy, setBusy] = useState(null); // "preview" | "apply"
    const [error, setError] = useState(null);
    const [over, setOver] = useState(false);
    const inputRef = useRef(null);
    const src = SOURCES.find((s) => s.value === source);

    const reset = () => {
        setFile(null);
        setPreview(null);
        setResult(null);
        setSelected(new Set());
        setError(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    const runPreview = async (f, force) => {
        setFile(f);
        setPreview(null);
        setResult(null);
        setError(null);
        setBusy("preview");
        try {
            const fd = new FormData();
            fd.append("file", f);
            const rep = await upload(src.route, fd, { dry_run: "true", force: force ? "true" : undefined });
            setPreview({ ...rep, force: !!force });
            setSelected(new Set((rep.rows || []).filter((r) => r.action === "created" || r.action === "updated").map((r) => r.key)));
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(null);
        }
    };

    const apply = async (onlySelected) => {
        setBusy("apply");
        setError(null);
        try {
            let rep;
            if (preview.file_id) {
                const body = { file_id: preview.file_id, dry_run: false };
                if (preview.force) body.force = true;
                if (onlySelected) body.rows = Array.from(selected);
                rep = await post(src.route, body);
            } else {
                // messages.csv is never stored: upload it again to apply.
                const fd = new FormData();
                fd.append("file", file);
                rep = await upload(src.route, fd, { dry_run: "false", force: preview.force ? "true" : undefined });
            }
            setResult(rep);
            setPreview(null);
            toast("Import applied.");
            bump();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(null);
        }
    };

    const onFiles = (files) => {
        const f = files && files[0];
        if (!f) return;
        runPreview(f, false);
    };

    const pickable = preview ? (preview.rows || []).filter((r) => r.action === "created" || r.action === "updated").length : 0;
    const writes = preview ? (preview.created || 0) + (preview.updated || 0) : 0;

    return (
        <div className="hz-stack">
            <Card title="Upload">
                <div className="hz-row-wrap" style={{ marginBottom: 14 }}>
                    <Segment options={SOURCES} value={source} onChange={(v) => { reset(); setSource(v); }} />
                </div>
                <Steps source={source} />
                <input ref={inputRef} type="file" accept={src.accept} style={{ display: "none" }} onChange={(e) => onFiles(e.target.files)} />
                {!preview && !result && (
                    <button
                        type="button"
                        className={"hz-drop" + (over ? " over" : "")}
                        disabled={!!busy}
                        onClick={() => inputRef.current && inputRef.current.click()}
                        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
                        onDragLeave={() => setOver(false)}
                        onDrop={(e) => { e.preventDefault(); setOver(false); onFiles(e.dataTransfer.files); }}
                    >
                        {busy === "preview" ? (
                            <>
                                <Spinner /> Reading {file && file.name}…
                            </>
                        ) : (
                            <>
                                <i className="ri-upload-2-line"></i>
                                Drop a file here or choose one
                            </>
                        )}
                    </button>
                )}
                {source === "linkedin" && (
                    <p className="hz-small hz-muted" style={{ margin: "12px 0 0" }}>
                        Nothing changes until the preview is applied. messages.csv only sets each person's last-messaged date; message text is never read or stored.
                    </p>
                )}
                {error && <div className="hz-alert hz-alert-danger" style={{ marginTop: 12 }}>{error}</div>}
            </Card>

            {preview && (
                <Card
                    noBody
                    title={`Preview · ${preview.filename || (file && file.name)} · ${KIND_LABEL[preview.kind] || preview.kind}`}
                    actions={<Button size="sm" variant="outline" onClick={reset}>Choose another file</Button>}
                >
                    <div className="hz-card-body">
                        <ReportCounts report={preview} />
                        {(preview.notes || []).length > 0 && (
                            <ul className="hz-small hz-muted" style={{ margin: "12px 0 0", paddingLeft: 18 }}>
                                {preview.notes.map((n, i) => <li key={i}>{n}</li>)}
                            </ul>
                        )}
                        {preview.already_imported && (
                            <div className="hz-alert hz-alert-info hz-row" style={{ marginTop: 12 }}>
                                <span className="hz-spacer">This file was already imported; nothing would change.</span>
                                <Button size="sm" variant="outline" onClick={() => runPreview(file, true)}>Compare row by row</Button>
                            </div>
                        )}
                        {(preview.tabs || []).length > 0 && (
                            <div style={{ marginTop: 12, display: "grid", gap: 4 }}>
                                {preview.tabs.map((t, i) => (
                                    <div key={i} className="hz-small">
                                        <b>{t.sheet || t.tab}</b>: {t.found === false ? "not found" : `${t.created || 0} created, ${t.updated || 0} updated, ${t.errors || 0} errors`}
                                        {t.unknown_columns && t.unknown_columns.length > 0 && <span className="hz-muted"> · unknown columns: {t.unknown_columns.join(", ")}</span>}
                                        {t.missing_required && t.missing_required.length > 0 && <span className="hz-error-text"> · missing: {t.missing_required.join(", ")}</span>}
                                        {t.problem && <span className="hz-error-text"> · {t.problem}</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <ReportRows report={preview} selectable={!!preview.file_id} selected={selected} onSelect={setSelected} />
                    <div className="hz-pager">
                        <span className="hz-small hz-muted">
                            {preview.file_id ? `${selected.size} of ${pickable} changes selected.` : "This file applies as a whole."}
                        </span>
                        <div className="hz-row">
                            {preview.file_id && (
                                <Button variant="outline" disabled={!!busy || selected.size === 0 || selected.size === pickable} onClick={() => apply(true)}>
                                    Apply selected
                                </Button>
                            )}
                            <Button variant="primary" disabled={!!busy || writes === 0} onClick={() => apply(false)}>
                                {busy === "apply" && <Spinner />}
                                Apply all
                            </Button>
                        </div>
                    </div>
                </Card>
            )}

            {result && (
                <Card noBody title="Import applied" actions={<Button size="sm" variant="outline" onClick={reset}>Import another file</Button>}>
                    <div className="hz-card-body">
                        <div className="hz-alert hz-alert-success" style={{ marginBottom: 12 }}>
                            <i className="ri-checkbox-circle-fill"></i> {result.filename}: {result.created || 0} created, {result.updated || 0} updated, {result.unchanged || 0} unchanged, {result.skipped || 0} skipped, {result.errors || 0} errors.
                        </div>
                        <ReportCounts report={result} />
                        {(result.notes || []).length > 0 && (
                            <ul className="hz-small hz-muted" style={{ margin: "12px 0 0", paddingLeft: 18 }}>
                                {result.notes.map((n, i) => <li key={i}>{n}</li>)}
                            </ul>
                        )}
                        <div className="hz-row" style={{ marginTop: 12 }}>
                            {result.role_changes > 0 && <Button size="sm" variant="soft" onClick={() => history.push("/today")}>Role changes on Today</Button>}
                            {result.possible_duplicates > 0 && <Button size="sm" variant="soft" onClick={() => history.push("/crm/imports/duplicates")}>Review duplicates</Button>}
                        </div>
                    </div>
                    <ReportRows report={result} />
                </Card>
            )}
        </div>
    );
}

function RunReport({ id, onClose }) {
    const { data, error, loading } = useLoad(() => get(`v1/crm/imports/${id}`), [id]);
    return (
        <Modal open title={data ? `${data.filename} · ${KIND_LABEL[data.kind] || data.kind}` : "Import run"} onClose={onClose} width={900} bodyStyle={{ padding: 0 }}>
            {loading && <Loading />}
            <ErrorBox error={error} />
            {data && (
                <>
                    <div className="hz-card-body">
                        <div className="hz-small hz-muted" style={{ marginBottom: 10 }}>
                            {formatDateTime(data.started_at)} · {data.source}{data.actor ? ` · ${data.actor}` : ""} · {data.status}
                            {data.selected_rows ? " · selected rows only" : ""}
                        </div>
                        <ReportCounts report={data} />
                        {(data.notes || []).length > 0 && (
                            <ul className="hz-small hz-muted" style={{ margin: "12px 0 0", paddingLeft: 18 }}>
                                {data.notes.map((n, i) => <li key={i}>{n}</li>)}
                            </ul>
                        )}
                    </div>
                    <ReportRows report={data} />
                </>
            )}
        </Modal>
    );
}

function Runs() {
    const [page, setPage] = useQueryState("page", 1);
    const [open, setOpen] = useState(null);
    const { version } = useShell();
    const { data, error, loading, reload } = useLoad(() => get("v1/crm/imports", { page, page_size: 25 }), [page, version]);
    const rows = (data && data.data) || [];
    return (
        <Card noBody title="Past imports">
            <ErrorBox error={error} onRetry={reload} />
            {loading && !data ? (
                <Loading />
            ) : (
                <div className="hz-table-wrap">
                    <table className="hz-table">
                        <thead>
                            <tr>
                                <th>When</th>
                                <th>File</th>
                                <th>Kind</th>
                                <th>By</th>
                                <th>Created</th>
                                <th>Updated</th>
                                <th>Unchanged</th>
                                <th>Skipped</th>
                                <th>Errors</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id}>
                                    <td className="hz-nowrap">{formatDateTime(r.started_at)}</td>
                                    <td>
                                        {r.filename}
                                        {r.already_imported && <Badge color="secondary" className="hz-xs" title="Same file as an earlier run">repeat</Badge>}
                                        {r.selected_rows && <> <Badge color="info">selection</Badge></>}
                                    </td>
                                    <td className="hz-nowrap">{KIND_LABEL[r.kind] || r.kind}</td>
                                    <td className="hz-nowrap">{r.source}{r.actor ? ` · ${r.actor}` : ""}</td>
                                    <td>{r.created}</td>
                                    <td>{r.updated}</td>
                                    <td>{r.unchanged}</td>
                                    <td>{r.skipped}</td>
                                    <td className={r.errors ? "hz-error-text" : ""}>{r.errors}</td>
                                    <td className="actions"><Button size="sm" variant="outline" onClick={() => setOpen(r.id)}>Report</Button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {rows.length === 0 && !error && <Empty>No imports yet.</Empty>}
                </div>
            )}
            {data && <Pager page={page} maxPage={data.max_page} count={data.count} onPage={setPage} label="runs" />}
            {open && <RunReport id={open} onClose={() => setOpen(null)} />}
        </Card>
    );
}

export default function Imports() {
    const { tab = "upload" } = useParams();
    const history = useHistory();
    useDocumentTitle("Imports");
    return (
        <>
            <PageHead crumb="CRM" title="Imports" sub="LinkedIn exports and the tracker workbook. Preview first, then apply." actions={<Link to="/crm/contacts" className="hz-btn hz-btn-outline">People</Link>} />
            <Card noBody style={{ marginBottom: 24 }}>
                <Tabs
                    active={tab}
                    onChange={(k) => history.push(k === "upload" ? "/crm/imports" : `/crm/imports/${k}`)}
                    tabs={[
                        { key: "upload", label: "Import", icon: "ri-upload-2-line" },
                        { key: "runs", label: "Past imports", icon: "ri-history-line" },
                        { key: "duplicates", label: "Duplicates", icon: "ri-git-merge-line" },
                    ]}
                />
            </Card>
            {tab === "upload" && <Upload />}
            {tab === "runs" && <Runs />}
            {tab === "duplicates" && <Duplicates />}
        </>
    );
}
